// src/components/Onboarding/InteractiveCanvas.js

import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';

// Import from modular canvas files
import {
  MAX_DPR,
  MAX_BACKING_PIXELS,
  HOTSPOT_UPDATE_INTERVAL,
  MASK_SCALE,
  SHIMMER_CONFIG,
} from './canvas/canvasConstants';

import {
  easeInOutCubic,
  getEffectiveDPR,
  calculateObjectFit,
  cropImageToMask,
} from './canvas/canvasUtils';

import { drawOverlay } from './canvas/overlayDrawing';

// Re-export SHIMMER_CONFIG for external consumers
export { SHIMMER_CONFIG } from './canvas/canvasConstants';

/**
 * INTERACTIVE CANVAS - Performance-Optimized Implementation
 * 
 * Robust canvas component for image display and manipulation with:
 * - Smooth aspect ratio transitions
 * - Image fit modes (contain/cover/fill like CSS object-fit)
 * - Accurate coordinate mapping
 * - Zoom/crop animations
 * - Overlay rendering with compositing-based masks
 * - Interactive hotspots
 * - DPR-capped rendering for mobile performance
 * - invalidate() scheduler to coalesce redraws
 */

export default function InteractiveCanvas({
  imageUrl,
  aspectRatio = 3 / 4,
  objectFit = 'contain',
  objectPosition = { x: 0.5, y: 0.5 }, // 0 = left/top, 0.5 = center, 1 = right/bottom
  cropRect = null,
  scale = 1.0, // Scale factor: 1.0 = 100%, 0.5 = 50%, etc.
  transitionDuration = 400,
  overlays = [],
  hotspots = [],
  hotspotsClickable = true, // Whether hotspot clicks are enabled (false = clicks pass through)
  onImageLoad = () => {},
  onImageError = () => {},
  onHotspotClick = () => {},
  onTransitionEnd = null, // Callback when both crop and aspect animations complete
  className = '',
  theme = 'light',
  debug = false,
  // NEW: Direct canvas refs for masks (performance optimization - no data URLs)
  bodyMaskCanvas = null, // Canvas element with body mask at MASK_SCALE resolution
  hairMaskCanvas = null, // Canvas element with hair mask at MASK_SCALE resolution
  // NEW: Edge glow effect - drawn directly on canvas, synced with shimmer
  edgeGlow = null, // { width: px, color: rgba string, opacity: 0-1 } or null to disable
  // NEW: Focus spotlight effect for hotspot click
  focusSpotlight = null, // { x, y } center point in image coordinates, or null to disable
  focusSpotlightOpacity = 0, // 0-1 opacity of the dark overlay (spotlight area stays clear)
  focusSpotlightRadius = 120, // Radius of spotlight circle in image pixels
  focusSpotlightColor = null, // Border color for the spotlight circle (hotspot color)
  onFocusDismiss = null, // Callback when clicking on the dark overlay to dismiss focus
}) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const rafRef = useRef(null);
  const fxCanvasRef = useRef(null); // Reusable offscreen canvas for compositing effects
  const overlayImagesRef = useRef(new Map());
  
  // Cached feathered mask canvases (generated once when mask/feather changes, not per-frame)
  const featheredBodyMaskCanvasRef = useRef(null);
  const featheredHairMaskCanvasRef = useRef(null);
  const lastFeatherConfigRef = useRef({ bodyMask: null, hairMask: null, featherPx: null });
  
  // Animation warm-up flag (prevents stuttering on first frame)
  const animationWarmedUpRef = useRef(false);
  
  // Invalidation state for coalesced rendering
  const invalidatedRef = useRef(false);
  const lastHotspotUpdateRef = useRef(0);
  
  // Visibility tracking for pausing animations when off-screen
  const [isVisible, setIsVisible] = useState(true);
  
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [overlayImagesLoaded, setOverlayImagesLoaded] = useState(false);
  const [maskLoadCount, setMaskLoadCount] = useState(0); // Force re-render counter
  
  // Container size
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  
  // Aspect ratio animation
  const [currentAspect, setCurrentAspect] = useState(aspectRatio);
  const [targetAspect, setTargetAspect] = useState(aspectRatio);
  const aspectAnimRef = useRef({ start: 0, progress: 1 });
  
  // Crop animation
  const [currentCrop, setCurrentCrop] = useState(null);
  const [targetCrop, setTargetCrop] = useState(null);
  const cropAnimRef = useRef({ start: 0, progress: 1 });

  // Load image - use refs to avoid re-running when callbacks change
  const onImageLoadRef = useRef(onImageLoad);
  const onImageErrorRef = useRef(onImageError);
  const onTransitionEndRef = useRef(onTransitionEnd);
  
  // Keep refs updated
  useEffect(() => {
    onImageLoadRef.current = onImageLoad;
    onImageErrorRef.current = onImageError;
    onTransitionEndRef.current = onTransitionEnd;
  }, [onImageLoad, onImageError, onTransitionEnd]);
  
  // Pre-create fxCanvas on mount to avoid first-frame allocation overhead
  // This eliminates stuttering when shimmer animation first starts
  useEffect(() => {
    if (!fxCanvasRef.current) {
      fxCanvasRef.current = document.createElement('canvas');
      // Pre-size to reasonable dimensions (will be resized as needed)
      fxCanvasRef.current.width = 400;
      fxCanvasRef.current.height = 400;
      // Prime the context to trigger GPU allocation
      const ctx = fxCanvasRef.current.getContext('2d');
      ctx.fillStyle = 'transparent';
      ctx.fillRect(0, 0, 1, 1);
    }
  }, []);
  
  useEffect(() => {
    if (!imageUrl) {
      setImageLoaded(false);
      imageRef.current = null;
      return;
    }

    // Skip if same image is already loaded
    if (imageRef.current && imageRef.current.src === imageUrl && imageLoaded) {
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      imageRef.current = img;
      setImageLoaded(true);
      setImageError(null);
      onImageLoadRef.current({ width: img.naturalWidth, height: img.naturalHeight });
    };
    
    img.onerror = (e) => {
      console.error('[InteractiveCanvas] Image load error');
      setImageError('Failed to load image');
      setImageLoaded(false);
      imageRef.current = null;
      onImageErrorRef.current(new Error('Failed to load image'));
    };
    
    img.src = imageUrl;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageUrl, imageLoaded]);

  // Load overlay images (including animation masks)
  useEffect(() => {
    let mounted = true;
    const loadPromises = [];
    
    overlays.forEach(overlay => {
      // Load regular image overlays
      if (overlay.type === 'image' && overlay.imageUrl) {
        if (!overlayImagesRef.current.has(overlay.imageUrl)) {
          const promise = new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              if (mounted) {
                overlayImagesRef.current.set(overlay.imageUrl, img);
              }
              resolve();
            };
            img.onerror = () => {
              console.warn('[InteractiveCanvas] Failed to load overlay image:', overlay.imageUrl);
              resolve();
            };
            img.src = overlay.imageUrl;
          });
          loadPromises.push(promise);
        }
      }
      
      // Load animation mask images (legacy maskUrl)
      if (overlay.type === 'animation' && overlay.maskUrl) {
        if (!overlayImagesRef.current.has(overlay.maskUrl)) {
          const promise = new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              if (mounted) {
                overlayImagesRef.current.set(overlay.maskUrl, img);
              }
              resolve();
            };
            img.onerror = () => {
              console.error('[InteractiveCanvas] Failed to load animation mask:', overlay.subtype);
              resolve();
            };
            img.src = overlay.maskUrl;
          });
          loadPromises.push(promise);
        }
      }
      
      // Load coloredArea mask images
      if (overlay.type === 'coloredArea' && overlay.maskUrl) {
        if (!overlayImagesRef.current.has(overlay.maskUrl)) {
          const promise = new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              if (mounted) {
                overlayImagesRef.current.set(overlay.maskUrl, img);
                // Mask loaded successfully
              }
              resolve();
            };
            img.onerror = () => {
              console.error('[InteractiveCanvas] Failed to load coloredArea mask:', overlay.mode);
              resolve();
            };
            img.src = overlay.maskUrl;
          });
          loadPromises.push(promise);
        }
      }
      
      // Load new multi-mask animation images (bodyMaskUrl and hairMaskUrl)
      if (overlay.type === 'animation') {
        if (overlay.bodyMaskUrl && !overlayImagesRef.current.has(overlay.bodyMaskUrl)) {
          const promise = new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              if (mounted) {
                overlayImagesRef.current.set(overlay.bodyMaskUrl, img);
              }
              resolve();
            };
            img.onerror = () => {
              console.error('[InteractiveCanvas] Failed to load body mask');
              resolve();
            };
            img.src = overlay.bodyMaskUrl;
          });
          loadPromises.push(promise);
        }
        
        if (overlay.hairMaskUrl && !overlayImagesRef.current.has(overlay.hairMaskUrl)) {
          const promise = new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              if (mounted) {
                overlayImagesRef.current.set(overlay.hairMaskUrl, img);
              }
              resolve();
            };
            img.onerror = () => {
              console.error('[InteractiveCanvas] Failed to load hair mask');
              resolve();
            };
            img.src = overlay.hairMaskUrl;
          });
          loadPromises.push(promise);
        }
      }
    });
    
    if (loadPromises.length > 0) {
      Promise.all(loadPromises).then(() => {
        if (mounted) {
          setOverlayImagesLoaded(true);
          // Force a re-render by incrementing counter
          setMaskLoadCount(prev => prev + 1);
        }
      });
    } else {
      setOverlayImagesLoaded(true);
    }
    
    return () => {
      mounted = false;
    };
  }, [overlays]);

  // Update target aspect ratio when prop changes
  useEffect(() => {
    if (Math.abs(aspectRatio - targetAspect) > 0.001) {
      setTargetAspect(aspectRatio);
      aspectAnimRef.current = { start: performance.now(), progress: 0 };
    }
  }, [aspectRatio, targetAspect]);

  // Update target crop when prop changes
  useEffect(() => {
    const newCrop = cropRect ? { ...cropRect } : null;
    const oldCrop = targetCrop;
    
    // Check if crop actually changed
    const changed = !newCrop !== !oldCrop || 
      (newCrop && oldCrop && (
        Math.abs(newCrop.x - oldCrop.x) > 0.1 ||
        Math.abs(newCrop.y - oldCrop.y) > 0.1 ||
        Math.abs(newCrop.width - oldCrop.width) > 0.1 ||
        Math.abs(newCrop.height - oldCrop.height) > 0.1
      ));
    
    if (changed) {
      setTargetCrop(newCrop);
      cropAnimRef.current = { start: performance.now(), progress: 0 };
    }
  }, [cropRect, targetCrop, currentCrop]);

  // Master animation loop - use refs to avoid dependency issues
  const currentCropRef = useRef(currentCrop);
  const targetCropRef = useRef(targetCrop);
  const currentAspectRef = useRef(currentAspect);
  const targetAspectRef = useRef(targetAspect);
  const transitionEndFiredRef = useRef(true); // Start as true since no animation pending
  
  // Keep refs updated
  useEffect(() => {
    currentCropRef.current = currentCrop;
  }, [currentCrop]);
  
  useEffect(() => {
    targetCropRef.current = targetCrop;
  }, [targetCrop]);
  
  useEffect(() => {
    currentAspectRef.current = currentAspect;
  }, [currentAspect]);
  
  useEffect(() => {
    targetAspectRef.current = targetAspect;
  }, [targetAspect]);
  
  useEffect(() => {
    let active = true;

    const animate = (now) => {
      if (!active) return;

      let needsUpdate = false;
      const cropWasAnimating = cropAnimRef.current.progress < 1;
      const aspectWasAnimating = aspectAnimRef.current.progress < 1;

      // Animate aspect ratio
      if (aspectAnimRef.current.progress < 1) {
        const elapsed = now - aspectAnimRef.current.start;
        const rawProgress = Math.min(1, elapsed / transitionDuration);
        const progress = easeInOutCubic(rawProgress);
        aspectAnimRef.current.progress = rawProgress;
        
        const current = currentAspectRef.current;
        const target = targetAspectRef.current;
        const interpolated = current + (target - current) * progress;
        setCurrentAspect(interpolated);
        needsUpdate = true;
        
        // Mark that we need to fire transition end when both complete
        if (rawProgress < 1) {
          transitionEndFiredRef.current = false;
        }
      }

      // Animate crop
      if (cropAnimRef.current.progress < 1) {
        const elapsed = now - cropAnimRef.current.start;
        const rawProgress = Math.min(1, elapsed / transitionDuration);
        const progress = easeInOutCubic(rawProgress);
        cropAnimRef.current.progress = rawProgress;

        const img = imageRef.current;
        if (img) {
          const fullRect = { x: 0, y: 0, width: img.naturalWidth, height: img.naturalHeight };
          const from = currentCropRef.current || fullRect;
          const to = targetCropRef.current || fullRect;

          const interpolated = {
            x: from.x + (to.x - from.x) * progress,
            y: from.y + (to.y - from.y) * progress,
            width: from.width + (to.width - from.width) * progress,
            height: from.height + (to.height - from.height) * progress,
          };
          setCurrentCrop(interpolated);
          needsUpdate = true;
          
          // Mark that we need to fire transition end when both complete
          if (rawProgress < 1) {
            transitionEndFiredRef.current = false;
          }
        }
      }

      // Check if BOTH animations just completed
      const cropNowComplete = cropAnimRef.current.progress >= 1;
      const aspectNowComplete = aspectAnimRef.current.progress >= 1;
      const bothComplete = cropNowComplete && aspectNowComplete;
      
      if (bothComplete && !transitionEndFiredRef.current && (cropWasAnimating || aspectWasAnimating)) {
        transitionEndFiredRef.current = true;
        // Fire callback with current mode (could determine from targetCrop)
        if (onTransitionEndRef.current) {
          const mode = targetCropRef.current ? 'zoomed' : 'full';
          onTransitionEndRef.current(mode);
        }
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      active = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [transitionDuration]); // Only depend on transitionDuration, not on animated values

  // Measure container size based on current aspect ratio and scale
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateSize = () => {
      const parent = container.parentElement;
      if (!parent) return;

      const parentWidth = parent.clientWidth;
      const maxWidth = Math.min(parentWidth, 800); // Max width before scaling
      const width = maxWidth * scale;
      const height = width / currentAspect;

      setContainerWidth(width);
      setContainerHeight(height);
    };

    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(container.parentElement || container);
    updateSize();

    return () => resizeObserver.disconnect();
  }, [currentAspect, scale]);

  // Visibility tracking - pause animations when off-screen or tab hidden
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    // Track both intersection AND document visibility
    let intersecting = true;
    let documentVisible = !document.hidden;
    
    const updateVisibility = () => {
      setIsVisible(intersecting && documentVisible);
    };
    
    // IntersectionObserver for off-screen detection
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        intersecting = entry.isIntersecting;
        updateVisibility();
      },
      {
        threshold: 0.1, // 10% visibility triggers
      }
    );
    observer.observe(container);
    
    // Document visibility for tab switching
    const handleVisibilityChange = () => {
      documentVisible = !document.hidden;
      updateVisibility();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // ========================================
  // FEATHERED MASK GENERATION (one-time, not per-frame)
  // ========================================
  // Generate pre-feathered mask canvases when mask or feather config changes.
  // This avoids applying blur() filter every frame in the render loop.
  useEffect(() => {
    // Get current maskFeatherPx from overlays (use first shimmer overlay's value or default)
    const shimmerOverlay = overlays.find(o => o.type === 'shimmer');
    const maskFeatherPx = shimmerOverlay?.maskFeatherPx ?? SHIMMER_CONFIG.maskFeatherPx;
    
    // Check if we need to regenerate (mask ref changed or feather value changed)
    const lastConfig = lastFeatherConfigRef.current;
    const bodyChanged = bodyMaskCanvas !== lastConfig.bodyMask;
    const hairChanged = hairMaskCanvas !== lastConfig.hairMask;
    const featherChanged = maskFeatherPx !== lastConfig.featherPx;
    
    if (!bodyChanged && !hairChanged && !featherChanged) {
      return; // No changes, skip regeneration
    }
    
    // Update tracking ref
    lastFeatherConfigRef.current = {
      bodyMask: bodyMaskCanvas,
      hairMask: hairMaskCanvas,
      featherPx: maskFeatherPx,
    };
    
    /**
     * Generate a feathered (blurred edge) version of a mask canvas.
     * @param {HTMLCanvasElement} srcMask - Original mask canvas
     * @param {number} featherPx - Blur radius in mask-canvas pixels
     * @returns {HTMLCanvasElement} New canvas with feathered edges
     */
    const generateFeatheredMask = (srcMask, featherPx) => {
      if (!srcMask || featherPx <= 0) {
        // No feathering needed, return original
        return srcMask;
      }
      
      // Create destination canvas same size as source
      const dst = document.createElement('canvas');
      dst.width = srcMask.width;
      dst.height = srcMask.height;
      const dstCtx = dst.getContext('2d');
      
      // Apply blur filter and draw source mask
      dstCtx.filter = `blur(${featherPx}px)`;
      dstCtx.drawImage(srcMask, 0, 0);
      dstCtx.filter = 'none';
      
      return dst;
    };
    
    // Generate feathered body mask if available
    if (bodyMaskCanvas && (bodyChanged || featherChanged)) {
      if (maskFeatherPx > 0) {
        featheredBodyMaskCanvasRef.current = generateFeatheredMask(bodyMaskCanvas, maskFeatherPx);
      } else {
        featheredBodyMaskCanvasRef.current = bodyMaskCanvas; // Use original if no feather
      }
    } else if (!bodyMaskCanvas) {
      featheredBodyMaskCanvasRef.current = null;
    }
    
    // Generate feathered hair mask if available
    if (hairMaskCanvas && (hairChanged || featherChanged)) {
      if (maskFeatherPx > 0) {
        featheredHairMaskCanvasRef.current = generateFeatheredMask(hairMaskCanvas, maskFeatherPx);
      } else {
        featheredHairMaskCanvasRef.current = hairMaskCanvas; // Use original if no feather
      }
    } else if (!hairMaskCanvas) {
      featheredHairMaskCanvasRef.current = null;
    }
    
    // Force redraw with new feathered masks
    invalidatedRef.current = true;
    
  }, [bodyMaskCanvas, hairMaskCanvas, overlays]);

  // Check if we have any active animations (animation or shimmer overlays)
  const hasActiveAnimations = useMemo(() => {
    return overlays.some(overlay => overlay.type === 'animation' || overlay.type === 'shimmer');
  }, [overlays]);

  // Render canvas - with animation loop support
  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    
    
    if (!canvas || !img || !imageLoaded || !containerWidth || !containerHeight) {
      return;
    }

    const ctx = canvas.getContext('2d');
    const effectiveDPR = getEffectiveDPR(containerWidth, containerHeight);
    let animationFrameId;

    // Calculate desired backing store size
    const desiredBackingW = Math.round(containerWidth * effectiveDPR);
    const desiredBackingH = Math.round(containerHeight * effectiveDPR);

    const render = () => {
      // Resize canvas backing store only if dimensions changed
      if (canvas.width !== desiredBackingW || canvas.height !== desiredBackingH) {
        canvas.width = desiredBackingW;
        canvas.height = desiredBackingH;
        canvas.style.width = `${containerWidth}px`;
        canvas.style.height = `${containerHeight}px`;
        // CRITICAL: Reset transform after resize - canvas loses state on resize
        ctx.setTransform(effectiveDPR, 0, 0, effectiveDPR, 0, 0);
      }
      
      // Clear in CSS space (transform handles DPR scaling)
      ctx.clearRect(0, 0, containerWidth, containerHeight);

      // Determine source rect (what part of image to show)
      const fullRect = { x: 0, y: 0, width: img.naturalWidth, height: img.naturalHeight };
      const srcRect = currentCrop || fullRect;
      
      // Crop is applied via srcRect

      // Calculate how to fit source rect into canvas
      const fit = calculateObjectFit(
        srcRect.width,
        srcRect.height,
        containerWidth,
        containerHeight,
        objectFit,
        objectPosition
      );

      // Draw image
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      ctx.drawImage(
        img,
        srcRect.x, srcRect.y, srcRect.width, srcRect.height,
        fit.x, fit.y, fit.width, fit.height
      );

      // Clip all overlays to the image bounds to prevent overflow
      ctx.save();
      ctx.beginPath();
      ctx.rect(fit.x, fit.y, fit.width, fit.height);
      ctx.clip();

      // Draw overlays - pass mask canvas refs for compositing-based rendering
      overlays.forEach(overlay => {
        drawOverlay(ctx, overlay, srcRect, fit, img.naturalWidth, img.naturalHeight, overlayImagesRef.current, {
          bodyMaskCanvas,
          hairMaskCanvas,
          featheredBodyMaskCanvas: featheredBodyMaskCanvasRef.current,
          featheredHairMaskCanvas: featheredHairMaskCanvasRef.current,
          fxCanvasRef,
        });
      });
      
      // Draw edge bleed effect - shimmer travels along horizontal edges
      // Vertical edges light up as one piece based on shimmer proximity
      if (edgeGlow && edgeGlow.width > 0 && edgeGlow.opacity > 0) {
        // Find shimmer overlay to sync with its timing
        const shimmerOverlay = overlays.find(o => o.type === 'shimmer');
        
        if (shimmerOverlay) {
          const periodMs = shimmerOverlay.periodMs ?? SHIMMER_CONFIG.periodMs;
          const now = performance.now();
          const phase = (now % periodMs) / periodMs; // 0-1, where shimmer band is
          
          const bandWidth = 0.35; // Width of the glowing region (same as shimmer)
          const w = edgeGlow.width;
          const x = fit.x;
          const y = fit.y;
          const width = fit.width;
          const height = fit.height;
          const baseOpacity = edgeGlow.opacity;
          
          ctx.save();
          ctx.lineCap = 'square';
          ctx.lineWidth = w;
          
          // Adjust phase to account for shimmer band starting off-screen
          // The shimmer band starts off-canvas and ends off-canvas
          // Advance the phase slightly so edge glow leads the shimmer
          const adjustedPhase = (phase + 0.02) / 0.84; // Tuned to sync with shimmer
          const clampedPhase = Math.max(0, Math.min(1, adjustedPhase));
          
          // Helper: create gradient that travels along an edge
          // glowCenter: 0-1 position along the edge where the glow is brightest
          const createTravelingGradient = (x1, y1, x2, y2, glowCenter) => {
            const grad = ctx.createLinearGradient(x1, y1, x2, y2);
            const halfBand = bandWidth / 2;
            
            // Add color stops for a smooth traveling glow
            const start = Math.max(0, glowCenter - halfBand);
            const peak = Math.max(0, Math.min(1, glowCenter));
            const end = Math.min(1, glowCenter + halfBand);
            
            grad.addColorStop(0, `rgba(255, 255, 255, 0)`);
            if (start > 0) {
              grad.addColorStop(start, `rgba(255, 255, 255, 0)`);
            }
            grad.addColorStop(peak, `rgba(255, 255, 255, ${baseOpacity})`);
            if (end < 1) {
              grad.addColorStop(end, `rgba(255, 255, 255, 0)`);
            }
            grad.addColorStop(1, `rgba(255, 255, 255, 0)`);
            
            return grad;
          };
          
          // TOP edge: glow travels left to right with adjusted phase
          const topGrad = createTravelingGradient(x, y, x + width, y, clampedPhase);
          ctx.strokeStyle = topGrad;
          ctx.beginPath();
          ctx.moveTo(x, y + w/2);
          ctx.lineTo(x + width, y + w/2);
          ctx.stroke();
          
          // BOTTOM edge: glow travels left to right with adjusted phase
          const bottomGrad = createTravelingGradient(x, y + height, x + width, y + height, clampedPhase);
          ctx.strokeStyle = bottomGrad;
          ctx.beginPath();
          ctx.moveTo(x, y + height - w/2);
          ctx.lineTo(x + width, y + height - w/2);
          ctx.stroke();
          
          // VERTICAL EDGES: Light up as one piece based on shimmer proximity
          // Left edge glows when shimmer is near left (phase ≈ 0-0.3)
          // Right edge glows when shimmer is near right (phase ≈ 0.7-1)
          const verticalEdgeOpacityScale = 0.5; // Reduce vertical edge intensity
          const getEdgeOpacity = (edgePosition) => {
            const distance = Math.abs(phase - edgePosition);
            const proximity = Math.max(0, 1 - distance / bandWidth);
            return baseOpacity * proximity * verticalEdgeOpacityScale;
          };
          
          const leftOpacity = getEdgeOpacity(0);
          const rightOpacity = getEdgeOpacity(1);
          
          // Left edge
          if (leftOpacity > 0.01) {
            ctx.strokeStyle = `rgba(255, 255, 255, ${leftOpacity})`;
            ctx.beginPath();
            ctx.moveTo(x + w/2, y);
            ctx.lineTo(x + w/2, y + height);
            ctx.stroke();
          }
          
          // Right edge
          if (rightOpacity > 0.01) {
            ctx.strokeStyle = `rgba(255, 255, 255, ${rightOpacity})`;
            ctx.beginPath();
            ctx.moveTo(x + width - w/2, y);
            ctx.lineTo(x + width - w/2, y + height);
            ctx.stroke();
          }
          
          ctx.restore();
        } else {
          // No shimmer overlay - show static subtle glow
          ctx.save();
          ctx.strokeStyle = `rgba(255, 255, 255, ${edgeGlow.opacity * 0.3})`;
          ctx.lineWidth = edgeGlow.width;
          ctx.strokeRect(
            fit.x + edgeGlow.width/2,
            fit.y + edgeGlow.width/2,
            fit.width - edgeGlow.width,
            fit.height - edgeGlow.width
          );
          ctx.restore();
        }
      }
      
      ctx.restore();
      // Note: We use setTransform for DPR scaling, not save/restore

      // Continue animation loop if we have active animations AND component is visible
      if (hasActiveAnimations && isVisible) {
        animationFrameId = requestAnimationFrame(render);
      }
    };

    // Start rendering (only if visible)
    if (isVisible) {
      render();
    }
    
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [imageLoaded, containerWidth, containerHeight, currentCrop, overlays, objectFit, objectPosition, debug, currentAspect, overlayImagesLoaded, hasActiveAnimations, maskLoadCount, isVisible, bodyMaskCanvas, hairMaskCanvas, edgeGlow]);

  // Map image coordinates to canvas coordinates
  const imageToCanvas = useCallback((imageX, imageY) => {
    const img = imageRef.current;
    if (!img || !imageLoaded) return { x: 0, y: 0 };

    const fullRect = { x: 0, y: 0, width: img.naturalWidth, height: img.naturalHeight };
    const srcRect = currentCrop || fullRect;

    // Check if point is within source rect
    if (imageX < srcRect.x || imageX > srcRect.x + srcRect.width ||
        imageY < srcRect.y || imageY > srcRect.y + srcRect.height) {
      return null; // Outside visible area
    }

    const fit = calculateObjectFit(
      srcRect.width,
      srcRect.height,
      containerWidth,
      containerHeight,
      objectFit,
      objectPosition
    );

    // Map from image space to canvas space
    const relX = (imageX - srcRect.x) / srcRect.width;
    const relY = (imageY - srcRect.y) / srcRect.height;

    return {
      x: fit.x + relX * fit.width,
      y: fit.y + relY * fit.height,
    };
  }, [imageLoaded, currentCrop, containerWidth, containerHeight, objectFit, objectPosition]);

  // Render hotspots (throttled to 20Hz during animations for performance)
  // Use useState + useEffect instead of useMemo to allow throttling
  const [renderedHotspots, setRenderedHotspots] = useState([]);
  const wasAnimatingRef = useRef(false);
  
  useEffect(() => {
    if (!imageLoaded || !containerWidth) {
      setRenderedHotspots([]);
      return;
    }
    
    // Throttle hotspot updates during animations (max 20Hz = every 50ms)
    const now = performance.now();
    const timeSinceLastUpdate = now - lastHotspotUpdateRef.current;
    
    // Check if animations are in progress
    const animating = cropAnimRef.current.progress < 1 || aspectAnimRef.current.progress < 1;
    
    // Detect animation end - force immediate update to snap to final positions
    const justFinishedAnimating = wasAnimatingRef.current && !animating;
    wasAnimatingRef.current = animating;
    
    if (animating && timeSinceLastUpdate < HOTSPOT_UPDATE_INTERVAL) {
      // Skip this update during animation, schedule next one
      const delay = HOTSPOT_UPDATE_INTERVAL - timeSinceLastUpdate;
      const timeoutId = setTimeout(() => {
        // Force re-run by resetting timestamp
        lastHotspotUpdateRef.current = 0;
      }, delay);
      return () => clearTimeout(timeoutId);
    }
    
    // Always update if: not animating, just finished animating, or throttle interval passed
    lastHotspotUpdateRef.current = now;
    
    const newHotspots = hotspots.map(hotspot => {
      // Hotspots now use centerX/centerY (image coords) + screenSize (fixed screen pixels)
      // This ensures consistent hotspot size regardless of image resolution
      
      // Map center point from image coordinates to canvas coordinates
      const center = imageToCanvas(hotspot.centerX, hotspot.centerY);
      
      if (!center) return null; // Center is outside visible area
      
      // screenSize is the fixed size in screen pixels (resolution-independent)
      const screenSize = hotspot.screenSize || 48; // Default 48px if not specified
      
      return {
        ...hotspot,
        // Position hotspot centered on the mapped point
        canvasX: center.x - screenSize / 2,
        canvasY: center.y - screenSize / 2,
        canvasWidth: screenSize,
        canvasHeight: screenSize,
      };
    }).filter(Boolean);
    
    setRenderedHotspots(newHotspots);
  }, [imageLoaded, containerWidth, hotspots, imageToCanvas, currentCrop, currentAspect]);

  return (
    <div className={`w-full ${className}`}>
      <div
        ref={containerRef}
        className="relative mx-auto transition-all duration-300 ease-in-out"
        style={{
          width: containerWidth ? `${containerWidth}px` : '100%',
          height: containerHeight ? `${containerHeight}px` : 'auto',
        }}
      >
        {/* Canvas */}
        {imageLoaded && (
          <canvas
            ref={canvasRef}
            className="absolute inset-0"
          />
        )}

        {/* Loading */}
        {!imageLoaded && !imageError && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded">
            <div className="text-gray-500 text-sm">Loading...</div>
          </div>
        )}

        {/* Error */}
        {imageError && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded">
            <div className="text-red-600 text-sm">{imageError}</div>
          </div>
        )}

        {/* Hotspots */}
        {renderedHotspots.map(hotspot => (
          <button
            key={hotspot.id}
            type="button"
            onTouchStart={(e) => {
              // Stop propagation on touch to prevent parent onTouchStart from firing
              e.stopPropagation();
            }}
            onClick={(e) => {
              // Always stop propagation to prevent parent canvas handlers from firing
              e.stopPropagation();
              
              // Get the screen position of the hotspot center
              const rect = e.currentTarget.getBoundingClientRect();
              const screenX = rect.left + rect.width / 2;
              const screenY = rect.top + rect.height / 2;
              const screenRadius = Math.max(rect.width, rect.height) / 2;
              
              // Always call onHotspotClick - let parent decide what to do based on mode
              // Parent can check hotspotsClickable to decide whether to trigger focus or collapse drawer
              onHotspotClick(hotspot.id, {
                screenX,
                screenY,
                screenRadius,
                color: hotspot.color,
              });
            }}
            className={`absolute ${hotspotsClickable ? 'hover:scale-110 active:scale-95' : ''}`}
            data-hotspot-id={hotspot.id}
            style={{
              left: `${hotspot.canvasX}px`,
              top: `${hotspot.canvasY}px`,
              width: `${hotspot.canvasWidth}px`,
              height: `${hotspot.canvasHeight}px`,
              transition: hotspotsClickable ? 'transform 0.2s ease-out' : 'none',
              cursor: hotspotsClickable ? 'pointer' : 'default',
            }}
            aria-label={hotspot.label || `Hotspot ${hotspot.id}`}
            tabIndex={hotspotsClickable ? 0 : -1}
            aria-hidden={!hotspotsClickable}
          >
            {hotspot.icon || (
              <div className="w-full h-full bg-blue-500 bg-opacity-30 rounded-full border-2 border-white shadow-lg flex items-center justify-center">
                <div className="w-2 h-2 bg-white rounded-full"></div>
              </div>
            )}
          </button>
        ))}
        
        {/* Focus Spotlight Overlay - REMOVED: Now rendered at viewport level in ImageAnalysisSlide */}
        {/* This eliminates edge seam issues between canvas and background darkening */}
      </div>
      
      {/* Debug Info */}
      {debug && imageLoaded && imageRef.current && (
        <div className="mt-2 p-3 bg-gray-900 text-green-400 rounded text-xs font-mono">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <div>Image: {imageRef.current.naturalWidth}×{imageRef.current.naturalHeight}</div>
            <div>Canvas: {containerWidth}×{containerHeight}</div>
            <div>Aspect: {currentAspect.toFixed(3)}</div>
            <div>Fit: {objectFit}</div>
            <div>Scale: {(scale * 100).toFixed(0)}%</div>
            <div>Max: 800px</div>
            <div className="col-span-2">
              Crop: {currentCrop ? `${currentCrop.x.toFixed(0)},${currentCrop.y.toFixed(0)} ${currentCrop.width.toFixed(0)}×${currentCrop.height.toFixed(0)}` : 'none'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
